import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

const IMPERSONATION_SESSION_KEY = "impersonation";

export interface ImpersonationState {
  organization: string | null;
  region: string | null;
  location: string | null;
}

interface ImpersonationContextType extends ImpersonationState {
  setImpersonation: (updates: Partial<ImpersonationState>) => void;
  clearImpersonation: () => void;
}

function loadImpersonationFromSession(): ImpersonationState {
  try {
    const stored = sessionStorage.getItem(IMPERSONATION_SESSION_KEY);
    if (!stored) return { organization: null, region: null, location: null };
    const parsed = JSON.parse(stored) as Partial<ImpersonationState>;
    return {
      organization: parsed.organization ?? null,
      region: parsed.region ?? null,
      location: parsed.location ?? null,
    };
  } catch {
    return { organization: null, region: null, location: null };
  }
}

function saveImpersonationToSession(state: ImpersonationState) {
  try {
    if (
      state.organization === null &&
      state.region === null &&
      state.location === null
    ) {
      sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
    } else {
      sessionStorage.setItem(IMPERSONATION_SESSION_KEY, JSON.stringify(state));
    }
  } catch {
    // Ignore sessionStorage errors (e.g. private browsing)
  }
}

const ImpersonationContext = createContext<ImpersonationContextType>({
  organization: null,
  region: null,
  location: null,
  setImpersonation: () => {},
  clearImpersonation: () => {},
});

export function ImpersonationProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [state, setState] = useState<ImpersonationState>(
    loadImpersonationFromSession,
  );

  const setImpersonation = useCallback((updates: Partial<ImpersonationState>) => {
    setState((prev) => {
      const next = { ...prev, ...updates };
      saveImpersonationToSession(next);
      return next;
    });
  }, []);

  const clearImpersonation = useCallback(() => {
    const cleared = { organization: null, region: null, location: null };
    saveImpersonationToSession(cleared);
    setState(cleared);
  }, []);

  const contextValue = useMemo(
    () => ({
      ...state,
      setImpersonation,
      clearImpersonation,
    }),
    [state, setImpersonation, clearImpersonation],
  );

  return (
    <ImpersonationContext.Provider value={contextValue}>
      {children}
    </ImpersonationContext.Provider>
  );
}

// Custom hook for using impersonation
export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error(
      "useImpersonation must be used within ImpersonationProvider",
    );
  }
  return context;
}
